import { type FC } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@/components/ui';
import { Package, Calendar, CreditCard, MapPin, User, Clock } from 'lucide-react';
import type { Order, OrderItem, PaymentStatus, OrderStatus, FulfillmentStatus } from '@/types';

export interface OrderDetailProps {
  order: Order & { items: OrderItem[] };
}

/**
 * OrderDetail component displays full order details
 * 
 * Requirements:
 * - 6.2: Show complete order details including items, prices, and payment status
 * - 6.3: Display tournament details and dates
 * - 6.4: Show addon details and quantities
 * - 6.5: Display payment status clearly
 * 
 * Usage:
 * ```tsx
 * import { OrderDetail } from '@/pages/components';
 * 
 * <OrderDetail order={orderWithItems} />
 * ```
 * 
 * The component displays:
 * - Order header with order number, dates, and status badges
 * - Complete list of order items with type-specific icons and details
 * - Tournament registrations with tournament name and price
 * - Addon purchases with quantity, variant, and pricing
 * - Order summary with subtotal, tax, and total
 * - Order timeline showing key events (placed, paid, cancelled, updated)
 * - Customer notes if present
 */
export const OrderDetail: FC<OrderDetailProps> = ({ order }) => {
  /**
   * Get badge variant for payment status
   * Requirements: 6.5 - Indicate payment status clearly
   */
  const getPaymentStatusBadge = (status: PaymentStatus) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-500">Failed</Badge>;
      case 'refunded':
        return <Badge className="bg-gray-500">Refunded</Badge>;
      case 'partially_refunded':
        return <Badge className="bg-gray-500">Partially Refunded</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  /**
   * Get badge variant for order status
   * Requirements: 6.5 - Indicate order status clearly
   */
  const getOrderStatusBadge = (status: OrderStatus) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500">Processing</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  /**
   * Get badge variant for fulfillment status
   */
  const getFulfillmentStatusBadge = (status: FulfillmentStatus) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-green-500">Delivered</Badge>;
      case 'shipped':
        return <Badge className="bg-blue-500">Shipped</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-500">Processing</Badge>;
      case 'pending':
        return <Badge className="bg-gray-500">Pending</Badge>;
      case 'pickup_ready':
        return <Badge className="bg-blue-500">Ready for Pickup</Badge>;
      case 'picked_up':
        return <Badge className="bg-green-500">Picked Up</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  /**
   * Format price in cents to dollars
   */
  const formatPrice = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  /**
   * Format date to readable string
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  /**
   * Format date and time to readable string
   */
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  /**
   * Get icon for item type
   */
  const getItemIcon = (itemType: string) => {
    switch (itemType) {
      case 'tournament':
        return <MapPin className="h-4 w-4" />;
      case 'activity':
        return <Calendar className="h-4 w-4" />;
      case 'addon':
        return <Package className="h-4 w-4" />;
      case 'special_event':
        return <Calendar className="h-4 w-4" />;
      case 'event_registration':
        return <User className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  /**
   * Get display name for item type
   */
  const getItemTypeDisplay = (itemType: string): string => {
    switch (itemType) {
      case 'tournament':
        return 'Tournament Registration';
      case 'activity':
        return 'Activity Registration';
      case 'addon':
        return 'Add-on';
      case 'special_event':
        return 'Special Event';
      case 'event_registration':
        return 'Event Registration';
      default:
        return itemType;
    }
  };

  /**
   * Check if item type requires physical fulfillment
   * Requirements: 6.5 - Show fulfillment status for physical items
   */
  const requiresFulfillment = (itemType: string): boolean => {
    return itemType === 'addon';
  };

  return (
    <div className="space-y-6">
      {/* Order Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1">
              <CardTitle className="text-xl sm:text-2xl font-viking mb-2">
                Order #{order.orderNumber}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>Placed on {formatDate(order.createdAt)}</span>
              </div>
            </div>
            <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 flex-wrap">
              {getPaymentStatusBadge(order.paymentStatus)}
              {getOrderStatusBadge(order.orderStatus)}
              {getFulfillmentStatusBadge(order.fulfillmentStatus)}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {order.paidAt && (
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">Paid on:</span>
                  <span className="font-medium">{formatDate(order.paidAt)}</span>
                </div>
              )}
              {order.trackingNumber && (
                <div className="flex items-center gap-2 text-sm">
                  <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">Tracking:</span>
                  <span className="font-medium break-all">{order.trackingNumber}</span>
                </div>
              )}
            </div>
            {order.items.some((item) => requiresFulfillment(item.itemType)) && (
              <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <div className="flex items-start gap-2">
                  <Package className="h-4 w-4 mt-0.5" />
                  <div>
                    <p className="font-medium">Fulfillment Status: {order.fulfillmentStatus}</p>
                    <p className="text-xs mt-1">
                      This order contains physical items that require fulfillment.
                      {order.trackingNumber && ' Check tracking information above for shipping updates.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Order Items */}
      <Card>
        <CardHeader>
          <CardTitle className="font-viking">Order Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="flex flex-col sm:flex-row sm:items-start sm:justify-between p-4 border rounded-lg gap-3"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {getItemIcon(item.itemType)}
                    <span className="text-xs text-muted-foreground uppercase">
                      {getItemTypeDisplay(item.itemType)}
                    </span>
                  </div>
                  <h4 className="font-medium mb-1">{item.itemName}</h4>
                  {item.itemDescription && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {item.itemDescription}
                    </p>
                  )}
                  {item.variantName && (
                    <div className="text-sm text-muted-foreground">
                      Variant: {item.variantName}
                    </div>
                  )}
                  {item.quantity > 1 && (
                    <div className="text-sm text-muted-foreground">
                      Quantity: {item.quantity}
                    </div>
                  )}
                  {item.discountCode && (
                    <div className="text-sm text-green-600 mt-1">
                      Discount applied: {item.discountCode} (-{formatPrice(item.discountAmount)})
                    </div>
                  )}
                  {requiresFulfillment(item.itemType) && (
                    <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Package className="h-3 w-3 flex-shrink-0" />
                      <span>Physical item - requires fulfillment</span>
                    </div>
                  )}
                </div>
                <div className="text-left sm:text-right sm:ml-4 flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                  <div>
                    {item.quantity > 1 && (
                      <div className="text-sm text-muted-foreground mb-1">
                        {formatPrice(item.unitPrice)} each
                      </div>
                    )}
                    <div className="font-bold">{formatPrice(item.total)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Order Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl font-viking">Order Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-muted-foreground">Tax</span>
              <span>{formatPrice(order.tax)}</span>
            </div>
            <div className="flex justify-between text-base sm:text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span>{formatPrice(order.total)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Order Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="font-viking">Order Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Order Created */}
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-medium">Order Placed</div>
                <div className="text-sm text-muted-foreground">
                  {formatDateTime(order.createdAt)}
                </div>
              </div>
            </div>

            {/* Payment Completed */}
            {order.paidAt && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <CreditCard className="h-5 w-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Payment Completed</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(order.paidAt)}
                  </div>
                  {order.paymentMethod && (
                    <div className="text-sm text-muted-foreground">
                      Method: {order.paymentMethod}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Cancelled */}
            {order.cancelledAt && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Package className="h-5 w-5 text-red-500" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Order Cancelled</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(order.cancelledAt)}
                  </div>
                </div>
              </div>
            )}

            {/* Last Updated */}
            {order.updatedAt !== order.createdAt && (
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">Last Updated</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(order.updatedAt)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer Notes */}
      {order.customerNotes && (
        <Card>
          <CardHeader>
            <CardTitle className="font-viking">Order Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{order.customerNotes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
